import React, { useCallback, useEffect, useState } from 'react';
import httpService from '../../services/httpService';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import UserAvatar from '../UserAvatar';
import KeyTakeawayImageWithPlaceholder from '../KeyTakeawayImageWithPlaceholder';
import { FaQuoteRight, FaFlag, FaBullseye, FaPaperPlane, FaSpinner, FaComments, FaSearch, FaTimes } from 'react-icons/fa';
import '../../styles/PracticeModal.scss';

const getPageNums = (cur, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const s = new Set([1, total]);
    for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) s.add(i);
    return [...s].sort((a, b) => a - b);
};

const SECTIONS = [
    { key: 'quote',          label: 'Quote',              icon: <FaQuoteRight style={{ fontSize: '0.65rem' }} /> },
    { key: 'insight',        label: 'Insight',            icon: <FaFlag       style={{ fontSize: '0.65rem' }} /> },
    { key: 'implementation', label: 'One way to try this',icon: <FaBullseye   style={{ fontSize: '0.65rem' }} /> },
];

const fmt = (dateStr, opts = { month: 'short', day: 'numeric' }) =>
    new Date(dateStr).toLocaleDateString('en-US', opts);

/* ─── Practice Detail Modal ─────────────────────────────────────────────────── */
const PracticeDetailModal = ({ practice, onHide, onCommentAdded }) => {
    const [comments, setComments] = useState(practice?.comments || []);
    const [body, setBody]         = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setComments(practice?.comments || []);
        setBody('');
    }, [practice?.id]);

    if (!practice) return null;

    const heroImg = practice.key_takeaway?.latest_image?.image_url || null;

    const submitReflection = () => {
        if (!body.trim()) return;
        setSubmitting(true);
        httpService.post(`/comments/${practice.id}`, { body })
            .then(res => {
                const newComment = res.data.comment;
                setComments(prev => [...prev, newComment]);
                onCommentAdded(practice.id, newComment);
                setBody('');
            })
            .catch(err => console.error('Error posting reflection:', err))
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal size="lg" show={!!practice} onHide={onHide} className="pm-modal">
            <div
                className={`pm-hero ${!heroImg ? 'pm-hero--no-image' : ''}`}
                style={heroImg ? { backgroundImage: `url(${heroImg})` } : undefined}
            >
                <div className="pm-hero__scrim">
                    <button
                        type="button"
                        className="btn-close btn-close-white pm-hero__close"
                        onClick={onHide}
                        aria-label="Close"
                    />
                    <div className="pm-hero__kicker">
                        Shared Practice{practice.episode?.title ? ` · ${practice.episode.title}` : ''}
                    </div>
                    <h5 className="pm-hero__title">
                        {practice.key_takeaway?.title || practice.title || 'Practice'}
                    </h5>
                    <span className={`pm-hero__status ${practice.completed ? 'pm-hero__status--done' : ''}`}>
                        {practice.completed ? '✓ Completed' : '◉ In Progress'}
                    </span>
                </div>
            </div>

            <Modal.Body>
                <Row className="g-3">
                    {/* Left: insight context */}
                    <Col lg={4}>
                        <div className="pm-insight__img">
                            <KeyTakeawayImageWithPlaceholder image={practice.key_takeaway?.latest_image} />
                        </div>
                        {SECTIONS.filter(({ key }) => practice.key_takeaway?.[key]).map(({ key, label, icon }) => (
                            <div key={key} className={`pm-insight__section pm-insight__section--${key}`}>
                                <div className="pm-insight__label">{icon}{label}</div>
                                <p className="pm-insight__text">{practice.key_takeaway[key]}</p>
                            </div>
                        ))}
                    </Col>

                    {/* Right: practice + reflections */}
                    <Col lg={8}>
                        {/* Listener + practice info */}
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <UserAvatar user={practice.listener_user} size={40} />
                            <div>
                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                    {practice.listener_user?.first_name} {practice.listener_user?.last_name}
                                </div>
                                <small className="text-muted">
                                    Shared {fmt(practice.shared_for_review, { month: 'long', day: 'numeric', year: 'numeric' })}
                                </small>
                            </div>
                            {practice.completed && (
                                <Badge bg="success" className="ms-auto" style={{ fontSize: '0.7rem' }}>Completed</Badge>
                            )}
                        </div>

                        {practice.title && (
                            <div className="pm-practice-view__title mb-1">{practice.title}</div>
                        )}
                        {practice.body && (
                            <div className="pm-practice-view__notes mb-3">{practice.body}</div>
                        )}

                        <hr className="my-3" />

                        {/* Reflections list */}
                        <div className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.82rem' }}>
                            Reflections ({comments.length})
                        </div>

                        <div style={{ maxHeight: '260px', overflowY: 'auto' }} className="mb-3">
                            {comments.length === 0 && (
                                <div className="text-center py-3 text-muted" style={{ fontSize: '0.82rem' }}>
                                    No reflections yet.
                                </div>
                            )}
                            {comments.map(comment => (
                                <div
                                    key={comment.id}
                                    className={`pm-comment ${comment.is_podcaster ? 'pm-comment--podcaster' : 'pm-comment--listener'}`}
                                >
                                    <div className="pm-comment__header">
                                        <UserAvatar user={comment.user} size={24} />
                                        <span className="pm-comment__name">
                                            {comment.user?.first_name} {comment.user?.last_name}
                                        </span>
                                        {comment.is_podcaster && (
                                            <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }}>Podcaster</Badge>
                                        )}
                                        <span className="pm-comment__date">{fmt(comment.created_at)}</span>
                                    </div>
                                    <p className="pm-comment__body">{comment.body}</p>
                                </div>
                            ))}
                        </div>

                        {/* Add reflection */}
                        <div className="d-flex gap-2 align-items-end">
                            <Form.Control
                                as="textarea"
                                rows={2}
                                size="sm"
                                placeholder="Write a reflection…"
                                value={body}
                                onChange={e => setBody(e.target.value)}
                                style={{ resize: 'none' }}
                            />
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={submitReflection}
                                disabled={submitting || !body.trim()}
                            >
                                {submitting ? <FaSpinner className="fa-spin" /> : <FaPaperPlane />}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" size="sm" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ─── Practice Tile ──────────────────────────────────────────────────────────── */
const PracticeTile = ({ practice, clubTitle, onOpen }) => {
    const lastComment = practice.comments?.length
        ? practice.comments[practice.comments.length - 1]
        : null;

    return (
        <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column">
                {/* Listener */}
                <Row className="justify-content-center align-items-center text-center mb-2">
                    <Col xs="auto">
                        <UserAvatar user={practice.listener_user} size={48} />
                    </Col>
                    <Col className="text-start ps-1">
                        <div className="fw-semibold" style={{ fontSize: '0.88rem', lineHeight: 1.2 }}>
                            {practice.listener_user?.first_name} {practice.listener_user?.last_name}
                        </div>
                        <small className="text-muted">
                            Shared {fmt(practice.shared_for_review)}
                        </small>
                    </Col>
                </Row>

                {/* Context breadcrumb */}
                <div className="text-center mb-2" style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)' }}>
                    {clubTitle && <span>{clubTitle}</span>}
                    {practice.episode?.title && <span> › {practice.episode.title}</span>}
                    {practice.key_takeaway?.title && (
                        <span className="d-block fw-semibold text-dark mt-1" style={{ fontSize: '0.78rem' }}>
                            {practice.key_takeaway.title}
                        </span>
                    )}
                </div>

                {/* Practice title/body */}
                {(practice.title || practice.body) && (
                    <div className="text-center mb-2">
                        {practice.title && (
                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{practice.title}</div>
                        )}
                        {practice.body && (
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                {practice.body.length > 80 ? practice.body.slice(0, 80) + '…' : practice.body}
                            </div>
                        )}
                    </div>
                )}

                {/* Stats */}
                <Row className="justify-content-center align-items-center text-center g-1 mb-2">
                    <Col xs="auto">
                        <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                            <FaComments className="me-1" />
                            {practice.comments?.length ?? 0} reflection{practice.comments?.length !== 1 ? 's' : ''}
                        </Badge>
                    </Col>
                    {practice.completed && (
                        <Col xs="auto">
                            <Badge bg="success" style={{ fontSize: '0.7rem' }}>Completed</Badge>
                        </Col>
                    )}
                    {practice.type && (
                        <Col xs="auto">
                            <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', border: '1px solid #dee2e6' }}>
                                {practice.type}
                            </Badge>
                        </Col>
                    )}
                </Row>

                {/* Last reflection */}
                {lastComment && (
                    <Row className="justify-content-center align-items-center text-center mb-2">
                        <Col xs="auto">
                            <UserAvatar user={lastComment.user} size={20} />
                        </Col>
                        <Col className="text-start ps-1">
                            <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                                Last reflection {fmt(lastComment.created_at)} by{' '}
                                {lastComment.user?.first_name} {lastComment.user?.last_name}
                                {lastComment.is_podcaster && (
                                    <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.58rem' }}>You</Badge>
                                )}
                            </small>
                        </Col>
                    </Row>
                )}

                <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-auto w-100"
                    onClick={() => onOpen(practice)}
                >
                    View Details
                </Button>
            </Card.Body>
        </Card>
    );
};

/* ─── Main Component ─────────────────────────────────────────────────────────── */
const PodcasterPodcastClubSharedPractices = ({ clubGuid }) => {
    const [loading, setLoading]                   = useState(true);
    const [practices, setPractices]               = useState([]);
    const [clubTitle, setClubTitle]               = useState('');
    const [listeners, setListeners]               = useState([]);
    const [pagination, setPagination]             = useState(null);
    const [error, setError]                       = useState(null);
    const [selectedPractice, setSelectedPractice] = useState(null);

    const [search, setSearch]               = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [listenerFilter, setListenerFilter] = useState('');
    const [page, setPage]                   = useState(1);

    // Debounce search → reset to page 1
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    // Reset page when listener filter changes
    const handleListenerFilter = (val) => { setListenerFilter(val); setPage(1); };

    const fetchPractices = useCallback(() => {
        if (!clubGuid) return;
        setLoading(true);
        setError(null);
        const params = { pageNumber: page, pageSize: 12 };
        if (debouncedSearch) params.search = debouncedSearch;
        if (listenerFilter)  params.listener_user_id = listenerFilter;
        httpService.get(`/dash/club/${clubGuid}/shared-practices`, { params })
            .then(res => {
                setPractices(res.data.data || []);
                setPagination(res.data.pagination || null);
                setClubTitle(prev => prev || res.data.club_title || '');
                // Only populate the dropdown on first load (no filters active)
                if (!debouncedSearch && !listenerFilter && res.data.listeners?.length) {
                    setListeners(res.data.listeners);
                }
            })
            .catch(err => setError(err.response?.data?.message || 'Error loading shared practices'))
            .finally(() => setLoading(false));
    }, [clubGuid, page, debouncedSearch, listenerFilter]);

    useEffect(() => { fetchPractices(); }, [fetchPractices]);

    const handleCommentAdded = (practiceId, newComment) => {
        setPractices(prev => prev.map(p =>
            p.id === practiceId
                ? { ...p, comments: [...(p.comments || []), newComment] }
                : p
        ));
        setSelectedPractice(prev =>
            prev?.id === practiceId
                ? { ...prev, comments: [...(prev.comments || []), newComment] }
                : prev
        );
    };

    const hasFilters = search || listenerFilter;
    const clearFilters = () => { setSearch(''); setListenerFilter(''); setPage(1); };

    return (
        <>
            {/* Filter bar */}
            <Row className="align-items-center g-2 mb-3">
                <Col xs={12} sm={6} md={5}>
                    <InputGroup size="sm">
                        <InputGroup.Text><FaSearch style={{ fontSize: '0.75rem' }} /></InputGroup.Text>
                        <Form.Control
                            placeholder="Search by title or notes…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <Button variant="outline-secondary" onClick={() => setSearch('')}>
                                <FaTimes style={{ fontSize: '0.75rem' }} />
                            </Button>
                        )}
                    </InputGroup>
                </Col>
                <Col xs={12} sm={6} md={4}>
                    <Form.Select
                        size="sm"
                        value={listenerFilter}
                        onChange={e => handleListenerFilter(e.target.value)}
                    >
                        <option value="">All listeners</option>
                        {listeners.map(l => (
                            <option key={l.user_id} value={l.user_id}>
                                {l.first_name} {l.last_name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                {hasFilters && (
                    <Col xs="auto">
                        <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                            Clear
                        </Button>
                    </Col>
                )}
            </Row>

            {/* Content */}
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </Spinner>
                </div>
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : practices.length === 0 ? (
                <Alert variant="info">
                    {hasFilters ? 'No practices match your filters.' : 'No practices have been shared for review yet.'}
                </Alert>
            ) : (
                <Row className="g-3 justify-content-center">
                    {practices.map(practice => (
                        <Col key={practice.id} xs={12} sm={6} lg={4}>
                            <PracticeTile
                                practice={practice}
                                clubTitle={clubTitle}
                                onOpen={setSelectedPractice}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && !loading && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <small className="text-muted">
                        Page {pagination.pageNumber} of {pagination.totalPages} ({pagination.total} total)
                    </small>
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={!pagination.hasPreviousPage}
                            onClick={() => setPage(p => p - 1)}
                        >←</Button>
                        {getPageNums(pagination.pageNumber, pagination.totalPages).reduce((acc, p, i, arr) => {
                            if (i > 0 && p - arr[i - 1] > 1) acc.push(<span key={`gap-${p}`} className="px-1 text-muted">…</span>);
                            acc.push(
                                <Button
                                    key={p}
                                    size="sm"
                                    variant={p === pagination.pageNumber ? 'secondary' : 'outline-secondary'}
                                    onClick={() => setPage(p)}
                                >{p}</Button>
                            );
                            return acc;
                        }, [])}
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={!pagination.hasNextPage}
                            onClick={() => setPage(p => p + 1)}
                        >→</Button>
                    </div>
                </div>
            )}

            <PracticeDetailModal
                practice={selectedPractice}
                onHide={() => setSelectedPractice(null)}
                onCommentAdded={handleCommentAdded}
            />
        </>
    );
};

export default PodcasterPodcastClubSharedPractices;
